// Pantalla de recuperación amigable para niños. Si la app falla o se queda en
// blanco (p. ej. archivos viejos en caché tras un deploy), en lugar de una
// pantalla en blanco mostramos un búho grande y UN solo botón gigante que
// limpia la caché y vuelve a empezar. Sin tecnicismos.
import React from "react";
import { clearCachesAndReload, attemptAutoRecover } from "@/lib/cacheBuster";

export default class KidErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // Si es un error de "chunk" (archivo viejo que ya no existe tras un deploy),
    // intentamos auto-recuperarnos, pero CON TOPE de intentos. Si
    // attemptAutoRecover devuelve false (ya se agotaron) NO recargamos más:
    // dejamos visible la pantalla de recuperación manual para romper bucles.
    // Revisamos message + name + stack porque Webpack a veces expone el texto
    // "Loading chunk 123 failed" en distintos campos según el navegador.
    const parts = [error?.message, error?.name, error?.stack, String(error || "")];
    const haystack = parts.filter(Boolean).join(" ");
    const isRecoverableError = /NotFoundError|removeChild|ChunkLoadError|Loading chunk\s+[\d]+\s+failed|Loading chunk|Loading CSS chunk|module script failed|Importing a module script failed|dynamically imported( module)?|failed to fetch dynamically imported/i.test(haystack);
    if (isRecoverableError) {
      attemptAutoRecover();
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    // Navegador incompatible: mostramos el aviso de actualización (el guard de
    // index.html ya puso window.__JASQ_UNSUPPORTED__), sin botón de reintento.
    if (typeof window !== "undefined" && window.__JASQ_UNSUPPORTED__) {
      return (
        <div
          data-testid="kid-unsupported-screen"
          style={{
            minHeight: "100vh", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", textAlign: "center",
            padding: "24px", background: "#F7F4FF", fontFamily: "Nunito, system-ui, sans-serif",
          }}
        >
          <div style={{ maxWidth: 520, background: "#fff", border: "3px solid #FFCC28", borderRadius: 20, padding: "32px 28px" }}>
            <div style={{ fontSize: 54, lineHeight: 1, marginBottom: 8 }} aria-hidden>🦉</div>
            <h1 style={{ color: "#A43790", fontSize: 22, margin: "0 0 10px" }}>Tu navegador es muy antiguo</h1>
            <p style={{ color: "#1F2547", fontSize: 15, lineHeight: 1.6, margin: "0 0 18px" }}>
              Para usar NEUROMATEMÁTICAS JASQ® necesitas un navegador actualizado (Google Chrome, Microsoft Edge o Firefox recientes) o un dispositivo más reciente.
            </p>
            <a href="https://www.google.com/chrome/" style={{ background: "#2C49A0", color: "#fff", textDecoration: "none", fontWeight: "bold", padding: "10px 18px", borderRadius: 999, fontSize: 14 }}>Descargar Chrome</a>
          </div>
        </div>
      );
    }
    return (
      <div
        data-testid="kid-error-screen"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "24px",
          background: "linear-gradient(160deg, #2C1A4A 0%, #A43790 60%, #FF7A59 100%)",
          color: "#fff",
          fontFamily: "Nunito, system-ui, sans-serif",
        }}
      >
        <div style={{ fontSize: "84px", lineHeight: 1, marginBottom: "8px" }} aria-hidden>
          🦉
        </div>
        <h1 style={{ fontSize: "30px", fontWeight: 800, margin: "8px 0", fontFamily: "Fredoka, Nunito, sans-serif" }}>
          ¡Ups! Algo se quedó dormido
        </h1>
        <p style={{ fontSize: "18px", maxWidth: 460, opacity: 0.95, margin: "0 0 28px 0" }}>
          No te preocupes. Toca el botón grande y Zarek lo arregla en un segundo. 🪄
        </p>
        <button
          data-testid="kid-error-recover-btn"
          onClick={() => clearCachesAndReload()}
          style={{
            fontSize: "22px",
            fontWeight: 800,
            color: "#1F2547",
            background: "#FFCC28",
            border: "4px solid #1F2547",
            borderRadius: "999px",
            padding: "18px 36px",
            cursor: "pointer",
            boxShadow: "6px 6px 0 #1F2547",
          }}
        >
          🔄 Volver a empezar
        </button>
      </div>
    );
  }
}
